import { motion } from "framer-motion";
import { Landmark, ShoppingBag, Factory, Stethoscope, Target } from "lucide-react";

// Animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const scaleItemVariants = {
  hidden: { opacity: 0, scale: 0.96, y: 25 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5 } },
};

const ReferencesSection = () => {
  const environments = [
    {
      title: "Banque / Finance",
      description: "Systèmes transactionnels critiques soumis à forte contrainte réglementaire.",
      tags: ["Sécurité DSP2", "Haute Disponibilité", "Faible Latence"],
      icon: <Landmark className="w-5 h-5 text-lenvia-blue" />,
      illustration: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&q=70"
    },
    {
      title: "Retail & E-commerce",
      description: "Plateformes haute performance absorbant des pics de charge massifs.",
      tags: ["Pics de Volumétrie", "Performance", "Résilience"],
      icon: <ShoppingBag className="w-5 h-5 text-lenvia-blue" />,
      illustration: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=70"
    },
    {
      title: "Industrie & Tech",
      description: "Supervision temps réel dans des environnements fortement contraints.",
      tags: ["IoT", "Environnements contraints", "Data temps réel"],
      icon: <Factory className="w-5 h-5 text-lenvia-blue" />,
      illustration: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&q=70"
    },
    {
      title: "Public / Santé",
      description: "Applications souveraines traitant des données critiques et sensibles.",
      tags: ["Hébergement HDS", "Conformité RGAA", "Souveraineté"],
      icon: <Stethoscope className="w-5 h-5 text-lenvia-blue" />,
      illustration: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=70"
    }
  ];

  return (
    <section className="section-spacing relative section-gradient-accent overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#070B1A]" />
      
      {/* Subtle ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(800px,140vw)] h-[600px] rounded-full bg-gradient-to-r from-lenvia-blue/5 to-lenvia-violet/5 blur-3xl" />

      <div id="secteurs" className="lenvia-container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2.5 mb-6">
            <Target className="w-4 h-4 text-lenvia-blue" />
            <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
              Secteurs cibles
            </span>
          </span>
          
          <h2 
            className="font-heading text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5 leading-[1.08]" 
            data-testid="references-title"
          >
            Des environnements où
            <br className="hidden md:block" />
            l'exigence est <span className="gradient-text">maximale</span>
          </h2>

          <p className="text-base lg:text-lg text-slate-400 max-w-2xl mx-auto">
            Notre expertise est conçue pour répondre aux contraintes spécifiques des secteurs les plus exigeants.
          </p>
        </motion.div>

        {/* Client Sectors */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {environments.map((env, index) => (
            <motion.div
              key={env.title}
              variants={scaleItemVariants}
              className="group"
              data-testid={`client-sector-${index}`}
            >
              <div className="glass-card p-0 flex flex-col justify-between h-full overflow-hidden">
                {/* Illustration header */}
                <div className="h-28 relative overflow-hidden">
                  <img 
                    src={env.illustration} 
                    alt="" 
                    className="w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500" 
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E] via-[#0A0F1E]/70 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-lenvia-blue/10 to-lenvia-violet/10" />
                </div>
                
                {/* Content */}
                <div className="p-7">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-5">
                    <div className="icon-container flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      {env.icon}
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-white mb-2">{env.title}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{env.description}</p>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2.5 pt-5 border-t border-white/[0.06]">
                    {env.tags.map(tag => (
                      <span 
                        key={tag} 
                        className="text-xs px-3.5 py-2 bg-white/[0.05] rounded-full text-slate-200 border border-white/[0.08] flex items-center gap-2 font-medium hover:bg-white/[0.08] transition-colors"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-lenvia-blue" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ReferencesSection;
